/*
 * Copyright (c) 2022 NEXTWELL INTL SRL. All Rights Reserved.
 * This software is the proprietary information of NEXTWELL INTL SRL.
 * Use is subject to license and non-disclosure terms
 */

import { HttpClient } from '@angular/common/http';
import { Injectable, signal } from '@angular/core';
import { LoggedInContextProvider, LoggedInUser } from '@irian-ro/auth-shared-module';
import { EnvironmentService } from '@irian-ro/util-environment';
import { BehaviorSubject, Observable, ReplaySubject, Subject, throwError } from 'rxjs';
import { filter } from 'rxjs/operators';
import { catchError, map, tap } from 'rxjs/operators';
import { LoggedInContext } from './logged-in-context.model';

@Injectable()
export class LoggedInContextService implements LoggedInContextProvider {

  // Signal-based state
  private _loggedInContext = signal<LoggedInContext>(new LoggedInContext());

  // Public signal (read-only)
  readonly loggedInContext = this._loggedInContext.asReadonly();

  // Observable for backward compatibility (replaces global-store userContext$)
  private _userContext$ = new BehaviorSubject<LoggedInContext>(new LoggedInContext());

  private currentUserStatus: ReplaySubject<LoggedInUser | undefined | null> = new ReplaySubject(1);

  constructor(
    private environmentService: EnvironmentService,
    private http: HttpClient
  ) {
    this.currentUserStatus.next(this._loggedInContext().loggedInUser);
  }

  // Observable accessor for consumers migrating from global-store
  // Filters out initial empty state - only emits when user context is actually loaded
  get userContext$(): Observable<LoggedInContext> {
    return this._userContext$.asObservable().pipe(
      filter((context) => !!context.loggedInUser?.authType)
    );
  }

  // Unfiltered observable that emits all state changes including logout (empty state)
  // Use this when you need to react to logout events (e.g., clearing menu)
  get userContextChanges$(): Observable<LoggedInContext> {
    return this._userContext$.asObservable();
  }

  getLoggedInContext(): Observable<LoggedInContext> {
    return this.userContext$;
  }

  getStoredLoggedInContext(): LoggedInContext {
    return this._loggedInContext();
  }

  clearLoggedInUser(): void {
    const empty = new LoggedInContext();
    this._loggedInContext.set(empty);
    this._userContext$.next(empty);
    this.currentUserStatus.next(empty.loggedInUser);
  }

  getLoggedInUserObservable(): Observable<LoggedInUser> {
    return this._userContext$.asObservable().pipe(
      map(context => context.loggedInUser)
    );
  }

  getLoggedInUser(): LoggedInUser {
    return this._loggedInContext().loggedInUser;
  }

  getCurrentUserStatus(): Subject<LoggedInUser | undefined | null> {
    return this.currentUserStatus;
  }

  nextCurrentUserStatus(loggedInUser: LoggedInUser): void {
    this.currentUserStatus.next(loggedInUser);
  }

  getLoggedInContextFromServer(): Observable<LoggedInUser> {
    let url = '/api/loggedIn';
    if (this.environmentService && this.environmentService.getEnvironmentProperty('loggedInContextUrl')) {
      url = `${this.environmentService.getEnvironmentProperty('loggedInContextUrl')}/loggedIn`;
    }

    return <Observable<LoggedInUser>>this.http.get<LoggedInContext>(url).pipe(
      tap((loggedInContext) => {
        this._loggedInContext.set(loggedInContext);
        this._userContext$.next(loggedInContext);
      }),
      map(loggedInContext => {
        return (<LoggedInContext>loggedInContext).loggedInUser;
      }),
      catchError((err) => {
        return throwError(() => err);
      })
    );
  }
}
